/**
 * Thinking Mode Types and Configuration
 *
 * This module defines the thinking modes available for AI research queries.
 * Each mode maps to different `thinking_on` and `thinking_budget` values
 * that control how deeply the AI reasons about the query.
 */

/**
 * Available thinking modes for research queries.
 * - 'fast': Quick responses without extended reasoning
 * - 'thinking': Standard reasoning for most queries (default for paid users previously)
 * - 'deep': Extended reasoning for complex legal questions
 */
export type ThinkingMode = 'fast' | 'thinking' | 'deep';

/**
 * Configuration for each thinking mode.
 */
interface ThinkingModeConfig {
  /** Lucide icon component name */
  icon: 'Zap' | 'Lightbulb' | 'Brain';
  /** i18n key for the mode label */
  labelKey: string;
  /** i18n key for the mode description */
  descriptionKey: string;
  /** Whether thinking is enabled for this mode */
  thinkingOn: boolean;
  /** Token budget for extended thinking (-1 for unlimited, 0 to disable) */
  thinkingBudget: number;
}

/**
 * Configuration mapping for all thinking modes.
 * Used by frontend for UI display and by API for sending correct values.
 */
export const THINKING_MODE_CONFIG: Record<ThinkingMode, ThinkingModeConfig> = {
  fast: {
    icon: 'Zap',
    labelKey: 'ThinkingMode.fast',
    descriptionKey: 'ThinkingMode.fastDescription',
    thinkingOn: false,
    thinkingBudget: 0,
  },
  thinking: {
    icon: 'Lightbulb',
    labelKey: 'ThinkingMode.thinking',
    descriptionKey: 'ThinkingMode.thinkingDescription',
    thinkingOn: true,
    thinkingBudget: 8192,
  },
  deep: {
    icon: 'Brain',
    labelKey: 'ThinkingMode.deepThinking',
    descriptionKey: 'ThinkingMode.deepThinkingDescription',
    thinkingOn: true,
    thinkingBudget: -1, // Unlimited
  },
};

/**
 * Ordered list of thinking modes for UI display.
 */
export const THINKING_MODES: ThinkingMode[] = ['fast', 'thinking', 'deep'];

/**
 * Default thinking mode for new users.
 */
export const DEFAULT_THINKING_MODE: ThinkingMode = 'fast';

/**
 * Access control for thinking modes.
 * Currently all modes are available to all users.
 * To restrict modes in the future, modify this function.
 *
 * @param mode - The thinking mode to check access for
 * @param isPaidUser - Whether the user has a paid subscription
 * @returns true if the user can access the mode
 *
 * @example
 * // Future restriction example:
 * // if (mode === 'deep') return isPaidUser;
 */
/**
 * Maps ThinkingMode to the zadanie usage field names.
 * 'fast' -> 'standard', 'thinking' -> 'full', 'deep' -> 'deep'
 */
export const THINKING_MODE_TO_ZADANIE: Record<
  ThinkingMode,
  'standard' | 'full' | 'deep'
> = {
  fast: 'standard',
  thinking: 'full',
  deep: 'deep',
};

export const canAccessThinkingMode = (
  mode: ThinkingMode,
  isPaidUser: boolean,
  paygBalance = 0,
  inTrial = false
): boolean => {
  if (mode === 'fast') return true;

  return isPaidUser || paygBalance > 0 || inTrial;
};
